(() => {
  if (customElements.get('campagne-volume-selector')) return;

  class CampagneVolumeSelector extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'nl-NL';
      this.productId = String(this.dataset.productId || '');
      this.defaultQty = Number(this.dataset.defaultQty || 1);
      this.barsPerUnit = Number(this.dataset.barsPerUnit || 2);
      this.unitSingular = this.dataset.unitSingular || 'doos';
      this.unitPlural = this.dataset.unitPlural || 'doosjes';
      this.barsLabel = this.dataset.barsLabel || 'bars';
      this.atcLabel = this.dataset.atcLabel || 'IN WINKELWAGEN';
      this.soldOutLabel = this.dataset.soldOutLabel || 'Houd mij op de hoogte';
      this.forceSoldOut = this.dataset.forceSoldOut === 'true';
      this.impactBadgeText = (this.dataset.impactBadgeText || '').trim();
      this.donationPerUnit = Number(this.dataset.donationPerUnit || 0);
      this.donationSuffix = this.dataset.donationSuffix || '';
      this.donationSuffixMobile = this.dataset.donationSuffixMobile || this.donationSuffix;

      this.quantities = this.parseQuantities(this.dataset.quantities || '1,3,5');
      this.variants = this.parseJson('[data-campagne-vs-variants-json]', []);

      this.tiersEl = this.querySelector('[data-campagne-vs-tiers]');
      this.atcButton = this.querySelector('[data-campagne-vs-atc]');
      this.atcLabelEl = this.querySelector('[data-campagne-vs-atc-label]');
      this.statusEl = this.querySelector('[data-campagne-vs-status]');

      this.buildTierButtons();
      this.tierButtons = [...this.querySelectorAll('[data-campagne-vs-tier]')];

      const defaultTier =
        this.tierButtons.find((btn) => Number(btn.dataset.quantity) === this.defaultQty) ||
        this.tierButtons[0];

      this.selectedQty = Number(defaultTier?.dataset.quantity || this.defaultQty);

      this.addEventListener('click', (event) => {
        const tierButton = event.target.closest('[data-campagne-vs-tier]');
        if (tierButton && this.contains(tierButton)) {
          if (tierButton.disabled || tierButton.getAttribute('aria-disabled') === 'true') return;
          this.selectTier(Number(tierButton.dataset.quantity));
          return;
        }

        const atc = event.target.closest('[data-campagne-vs-atc]');
        if (atc && this.contains(atc)) {
          this.handleAtc();
        }
      });

      document.addEventListener('variant:change', () => this.render());
      document.addEventListener('pb:variant-change', () => this.render());

      const scope = this.productScope();
      if (scope) {
        scope.addEventListener('change', (event) => {
          if (event.target?.matches?.('input[name^="options"]')) {
            this.render();
          }
        });
      }

      this.render();
    }

    parseQuantities(raw) {
      return String(raw)
        .split(',')
        .map((part) => Number(String(part).trim()))
        .filter((qty) => qty > 0);
    }

    parseJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent || 'null') || fallback;
      } catch (error) {
        console.warn('[CampagneVolumeSelector] Failed to parse JSON', selector, error);
        return fallback;
      }
    }

    productScope() {
      return (
        this.closest('.product-information') ||
        this.closest('.product-details') ||
        document
      );
    }

    buildTierButtons() {
      if (!this.tiersEl) return;

      this.tiersEl.replaceChildren();

      for (const [index, qty] of this.quantities.entries()) {
        const selected = qty === this.defaultQty;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `campagne-vs__tier${selected ? ' is-selected' : ''}`;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.dataset.campagneVsTier = '';
        button.dataset.quantity = String(qty);

        const bars = qty * this.barsPerUnit;
        const label = `${qty} ${this.unitNoun(qty)}`;
        const subtitle = `(${bars} ${this.barsLabel})`;

        button.innerHTML =
          `<span class="campagne-vs__label">` +
          `<span class="campagne-vs__qty">${label}</span>` +
          `<span class="campagne-vs__bars"> ${subtitle}</span>` +
          `</span>` +
          `<span class="campagne-vs__price" data-campagne-vs-price></span>` +
          `<span class="campagne-vs__check" aria-hidden="true">${this.checkMarkup()}</span>`;

        // Same badges as boobie-buy: impact badge on option 3, donation pill on every option
        if (this.impactBadgeText && index === 2) {
          button.prepend(this.badgeElement('campagne-vs__impact', this.impactBadgeText));
        }

        const donation = qty * this.donationPerUnit;
        if (donation > 0) button.appendChild(this.donationElement(donation));

        this.tiersEl.appendChild(button);
      }
    }

    badgeElement(className, text) {
      const badge = document.createElement('span');
      badge.className = className;
      badge.textContent = text;
      return badge;
    }

    donationElement(amount) {
      const pill = document.createElement('span');
      pill.className = 'campagne-vs__donation';

      const heart = this.querySelector('[data-campagne-vs-heart-template]');
      if (heart) pill.appendChild(heart.content.cloneNode(true));

      // Donation is a fixed euro amount (not converted), formatted like boobie-buy: “€25”
      const amountText = `€${amount}`;
      pill.appendChild(
        this.badgeElement(
          'campagne-vs__donation-text campagne-vs__donation-text--desktop',
          `${amountText} ${this.donationSuffix}`.trim(),
        ),
      );
      pill.appendChild(
        this.badgeElement(
          'campagne-vs__donation-text campagne-vs__donation-text--mobile',
          `${amountText} ${this.donationSuffixMobile}`.trim(),
        ),
      );
      return pill;
    }

    checkMarkup() {
      const existing = this.querySelector('[data-campagne-vs-check-template]');
      if (existing) return existing.innerHTML;
      return (
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<rect x="1" y="1" width="20" height="20" rx="10" fill="currentColor"/>' +
        '<path d="M6 11L9.5 14.5L16 7" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>'
      );
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (error) {
        return `€${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    unitNoun(qty) {
      return Number(qty) === 1 ? this.unitSingular : this.unitPlural;
    }

    currentVariant() {
      const scope = this.productScope();

      const checked = scope.querySelector(
        `variant-picker[data-product-id="${this.productId}"] input[name^="options"]:checked[data-variant-id]`
      );
      if (checked?.dataset.variantId) {
        const fromList = this.variants.find((variant) => String(variant.id) === String(checked.dataset.variantId));
        if (fromList) return fromList;
        return {
          id: checked.dataset.variantId,
          price: Number(checked.dataset.price || 0),
          available: checked.dataset.optionAvailable !== 'false',
          inventory_quantity: null,
          inventory_management: null,
          inventory_policy: 'deny',
        };
      }

      const formInput = scope.querySelector(
        `product-form-component[data-product-id="${this.productId}"] input[name="id"], form[action^="/cart/add"] input[name="id"]`
      );
      if (formInput?.value) {
        return this.variants.find((variant) => String(variant.id) === String(formInput.value)) || this.variants[0] || null;
      }

      return this.variants.find((variant) => variant.available) || this.variants[0] || null;
    }

    inventoryAllows(variant, qty) {
      if (!variant) return false;
      if (this.forceSoldOut) return false;
      if (variant.available === false) return false;

      const managed = Boolean(variant.inventory_management);
      if (!managed) return true;

      if (variant.inventory_policy === 'continue') return true;

      const stock = Number(variant.inventory_quantity);
      if (Number.isNaN(stock)) return variant.available !== false;

      return stock >= Number(qty);
    }

    selectTier(qty) {
      if (!qty || Number(qty) === Number(this.selectedQty)) return;
      this.selectedQty = Number(qty);
      this.render();
    }

    syncQuantityInputs() {
      const scope = this.productScope();
      const qty = String(this.selectedQty);

      for (const input of scope.querySelectorAll(
        'input[name="quantity"], quantity-selector-component input, .quantity__input'
      )) {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    updateHeaderPrice(totalCents) {
      const priceHtml = `<span class="price">${this.formatMoney(totalCents)}</span>`;

      for (const priceRoot of document.querySelectorAll('#pdp-price, .mobileblock .secondblock')) {
        priceRoot.innerHTML = priceHtml;
      }
    }

    render() {
      const variant = this.currentVariant();
      const unitPrice = Number(variant?.price || 0);
      const qty = Number(this.selectedQty);
      const soldOut = this.forceSoldOut || variant?.available === false;

      for (const button of this.tierButtons) {
        const buttonQty = Number(button.dataset.quantity);
        const selected = buttonQty === qty;
        const stockOk = this.inventoryAllows(variant, buttonQty);
        const priceEl = button.querySelector('[data-campagne-vs-price]');

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.classList.toggle('is-disabled', !stockOk);
        button.disabled = !stockOk;
        button.setAttribute('aria-disabled', stockOk ? 'false' : 'true');

        if (priceEl) priceEl.textContent = this.formatMoney(unitPrice * buttonQty);
      }

      if (!this.inventoryAllows(variant, qty)) {
        const fallback = this.tierButtons.find((btn) => this.inventoryAllows(variant, Number(btn.dataset.quantity)));
        if (fallback && Number(fallback.dataset.quantity) !== qty) {
          this.selectedQty = Number(fallback.dataset.quantity);
          return this.render();
        }
      }

      this.syncQuantityInputs();
      this.updateHeaderPrice(unitPrice * Number(this.selectedQty));

      if (this.atcButton) {
        const canAdd = Boolean(variant?.id) && this.inventoryAllows(variant, this.selectedQty);
        this.atcButton.disabled = !canAdd && !soldOut;
        this.atcButton.classList.toggle('is-sold-out', soldOut || !canAdd);
        this.atcButton.classList.toggle('klaviyo-bis-trigger', soldOut || !canAdd);

        if (this.atcLabelEl) {
          this.atcLabelEl.textContent = soldOut || !canAdd ? this.soldOutLabel : this.atcLabel;
        }
      }

      if (this.statusEl) this.statusEl.textContent = '';
    }

    async handleAtc() {
      const variant = this.currentVariant();
      const qty = Number(this.selectedQty);

      if (this.atcButton?.classList.contains('klaviyo-bis-trigger')) {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', 'W2Syx5']);
        return;
      }

      if (!variant?.id || qty < 1 || !this.inventoryAllows(variant, qty)) return;

      if (this.atcButton) this.atcButton.disabled = true;
      if (this.atcLabelEl) this.atcLabelEl.textContent = '...';
      if (this.statusEl) this.statusEl.textContent = '';

      try {
        if (window.PBCart?.addLine) {
          await window.PBCart.addLine(variant.id, qty);
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(variant.id), quantity: qty }),
          });
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      } catch (error) {
        console.warn('[CampagneVolumeSelector] Add to cart failed', error);
        if (this.statusEl) this.statusEl.textContent = 'Kon niet toevoegen. Probeer opnieuw.';
      } finally {
        if (this.atcLabelEl) this.atcLabelEl.textContent = this.atcLabel;
        this.render();
      }
    }
  }

  customElements.define('campagne-volume-selector', CampagneVolumeSelector);
})();
